// Logique « headless » du récapitulatif de commande.
//
// Extraite de CheckoutForm.tsx (sans changement de comportement) pour être
// partagée entre l'ancien rendu (CheckoutForm) et le Nouveau Panier
// (screens/Checkout/v2, activé par le toggle BO « Activer le nouveau
// Panier »). Tout ce qui touche au paiement — SCA / 3DS, Alma, carte cadeau,
// code promo, tracking GA4, remontées Sentry, redirections — vit ici et
// UNIQUEMENT ici : les deux rendus ne font que de l'affichage.
import { useToast } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import * as Sentry from '@sentry/react';
import { sentryTrace } from 'utils/sentryTrace';
import {
  useFetchAvailablePaymentPlans,
  useFetchClientCompleteInfo,
  useFetchCustomPaymentMethods,
  useListPurchases,
} from 'hooks/useApiQueries';
import { useUserContext } from 'providers/useUserContext';
import { useTranslation } from 'react-i18next';
import { Location, useLocation, useNavigate } from 'react-router';
import { ApiClientExceptionErrorCode } from 'types/api';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP } from 'utils/http';
import { resolveUserError } from 'utils/userErrorMessages';
import { trackAddToCart, trackBeginCheckout, trackPurchase, stashPurchaseItem, stashPurchaseCustomer, CustomerInput } from 'utils/tracking';
import { hasMarketingConsent } from 'utils/consent';
import { setScaPayment, newScaAttemptId, getOrCreateDeviceId } from 'utils/scaStorage';
import { safeSessionStorage } from 'utils/safeStorage';
import { ContractProduct, GiftCardProduct, ServiceProduct } from '.';
import { isCardValidThroughContractEngagement } from './utils/cardExpirationCheck';
import { isApplePayCapable, isGooglePayCapable } from 'utils/wallets';
import type { WalletPaymentStep } from 'components/WalletPaymentOverlay';

/** Valeur de `PaymentMethod` envoyée au serveur pour Apple Pay (SaleService::APPLE_PAY_METHOD_NAME). */
export const APPLE_PAY_METHOD = 'Apple Pay';
/** Idem pour Google Pay. */
export const GOOGLE_PAY_METHOD = 'Google Pay';
/** Les deux portefeuilles, qui suivent le même chemin de paiement. */
export const WALLET_METHODS: string[] = [APPLE_PAY_METHOD, GOOGLE_PAY_METHOD];

export type Product = ServiceProduct | GiftCardProduct | ContractProduct;
export type CheckoutVariant = 'service' | 'contract' | 'giftcard';

export interface ContractAgreementProps {
  pdfUrl?: string | null;
  agreementTerms?: string | null;
}

export interface PaymentMethod {
  planDetails: Record<string, string | number>[];
  maxAmount: number;
  minAmount: number;
  installmentsCount: number;
  Id: number;
  Name: string;
}

export interface CheckoutData {
  AlmaPaymentId: string;
  AlmaPaymentStatus: string;
  InstallmentsCount: number;
  ServiceId: number;
  Amount: number;
  TotalAmount: number;
  DiscountedAmount: number;
  GiftCard: string;
  GiftCardBalance?: number;
  BillingName: string;
  BillingLastName: string;
  BillingAddress: string;
  BillingCity: string;
  BillingPostalCode: string;
  CreditCardNumber: string;
  Exp: string;
  Cryptogram: string;
  SaveInfo: boolean;
  StoredCreditCard: 'new' | 'saved' | 'gift-card';
  PromoCode: string;
  PaymentMethod: string;
  Email: string;
  Phone: string;
  /**
   * Consentement marketing du visiteur au moment du paiement, lu sur le
   * bandeau de cookies du site. Le serveur s'en sert pour décider s'il peut
   * doubler l'achat d'un événement Meta Conversions API. Transite dans
   * `Payment` sur tous les endpoints d'achat, y compris à travers le 3DS.
   */
  MarketingConsent: boolean;
  // Nouveaux champs pour SCA
  Sca3DSecureRequired?: boolean;
  ScaExemptionRequested?: string;
  ScaAuthenticationUrl?: string;
  ScaTransactionId?: string;
  ScaReturnUrl?: string;
}

/**
 * Résultat remonté à `onSuccess` (Nouveau Panier v2). `comp` = total à 0 €
 * couvert par un code promo / une carte cadeau ; `paid` = paiement CB ou
 * moyen custom abouti sans challenge 3DS (le cas 3DS et le cas Alma sortent
 * de la page : ScaCallback / App.tsx prennent le relais au retour).
 */
export type CheckoutSuccess =
  | { kind: 'comp'; response: Record<string, unknown> }
  | { kind: 'paid'; paymentMethod: string };

export interface CheckoutFormProps {
  amount: number;
  onPurchase: (data: CheckoutData) => Promise<Record<string, unknown>>; // Ensure this type is consistent
  product?: Product;
  /**
   * Type de produit en cours de checkout. Conditionne l'affichage des moyens
   * de paiement (Alma/giftcard désactivés en mode contract), force la
   * sauvegarde de carte (autopay obligatoire) et active la checkbox CGV.
   * Défaut 'service' pour rétrocompatibilité.
   */
  variant?: CheckoutVariant;
  /**
   * Props transmises à `ContractAgreementCheckbox` (CGV). Uniquement
   * exploité en variant='contract'.
   */
  contractAgreement?: ContractAgreementProps;
  /**
   * Nouveau Panier (v2) uniquement : quand fourni, le hook n'affiche ni
   * toast ni redirection après un paiement abouti et délègue la suite à
   * l'appelant (étape « Confirmation » dans la page). Absent → comportement
   * historique (toast + redirection vers Mon compte).
   */
  onSuccess?: (result: CheckoutSuccess) => void;
}


export function useCheckoutForm({
  amount,
  onPurchase,
  product: propProduct,
  variant = 'service',
  contractAgreement,
  onSuccess,
}: CheckoutFormProps) {
  const isContract = variant === 'contract';
  const { data: customPaymentMethods } = useFetchCustomPaymentMethods();
  const { data: paymentPlans, isLoading: isLoadingPaymentPlans } = useFetchAvailablePaymentPlans(amount);
  const [paymentMethod, setPaymentMethod] = useState('Credit Card');
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [isRefetchingPaymentPlans, setIsRefetchingPaymentPlans] = useState(false);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [almaRedirectInProgress, setAlmaRedirectInProgress] = useState(false);
  const [showFallbackRedirect, setShowFallbackRedirect] = useState(false);
  const fallbackTimerRef = useRef<NodeJS.Timeout | null>(null);
  const forceRedirectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();
  const { isLoggedIn, user } = useUserContext();
  const toast = useToast();
  const { data: clientCompleteInfo } = useFetchClientCompleteInfo();
  const { data: clientPurchases } = useListPurchases() || {};
  const { state } = useLocation() || {};
  const { from, product: stateProduct } = (state || {}) as { from?: Location; product?: Product };

  // Utiliser le produit en props en priorité, sinon celui du state
  const product = propProduct || stateProduct;

  // Mindbody refuse lui-même le second achat d'une offre d'essai (400
  // « Client has hit the purchase count for this intro series »). Ce test sert
  // uniquement à désactiver le bouton en amont, pour éviter au client de saisir
  // sa carte pour rien et de se prendre le message d'erreur brut de Mindbody.
  // Il comparait avant le mot « bienvenue » dans les services actifs du client,
  // ce qui ne matchait jamais une « Offre d'essai ... » : il ne se déclenchait
  // donc jamais. On compare désormais l'Id de la pricing option dans
  // l'historique d'achats, hors lignes remboursées et avoirs.
  const productIsIntroOffer = product?.type === 'service' && product.IsIntroOffer;
  const productServiceId = product?.type === 'service' ? String(product.Id) : null;
  const hasAlreadyBoughtThisIntroOffer = Boolean(
    productServiceId &&
      clientPurchases?.some((purchase) =>
        purchase.Sale?.PurchasedItems?.some(
          (item) => String(item.Id) === productServiceId && !item.Returned && item.TotalAmount >= 0,
        ),
      ),
  );
  const disableConfirmAlmaPayment = productIsIntroOffer && hasAlreadyBoughtThisIntroOffer;

  // GA4 add_to_cart + begin_checkout : émis une fois à l'arrivée sur le
  // récapitulatif de commande (il n'y a pas d'étape panier séparée dans ce
  // tunnel, l'entrée checkout vaut donc ajout au panier ET début de checkout).
  useEffect(() => {
    if (product) {
      const p = product as unknown as Record<string, unknown>;
      trackAddToCart(p, amount);
      trackBeginCheckout(p, amount);
    }
    // une seule émission au montage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();

  const client = clientCompleteInfo?.Client;
  const clientCreditCard = client?.ClientCreditCard;
  const clientHasStoredCreditCard = !!clientCreditCard?.CardNumber;

  // Identité de l'acheteur pour le bloc customer du purchase (option BO
  // « Données client dans purchase »). trackPurchase/stashPurchaseCustomer
  // l'ignorent tant que l'option est décochée : aucun changement sans opt-in.
  const checkoutCustomer = (data?: { Email?: string; Phone?: string }): CustomerInput => ({
    email: data?.Email || user?.Email,
    phone: data?.Phone || user?.MobilePhone,
    firstName: user?.FirstName ?? client?.FirstName,
    lastName: user?.LastName ?? client?.LastName,
  });

  const [updatedPaymentPlans, setUpdatedPaymentPlans] = useState<PaymentMethod[] | null>(null);

  // Étape affichée par l'overlay du paiement par portefeuille. 'idle' tant que
  // le client n'a pas lancé le paiement : l'overlay reste fermé.
  const [walletStep, setWalletStep] = useState<WalletPaymentStep>('idle');

  // Billetterie (landing) : pas d'Alma. Le plafond de places est contrôlé au
  // moment de la vente, alors qu'Alma encaisse avant : un client pourrait
  // payer un ticket qui vient de partir.
  const isLandingProduct = !!(propProduct as { fromLanding?: boolean } | undefined)?.fromLanding;
  const isAlmaAvailable = !isContract && !isLandingProduct;

  // Portefeuilles : deux conditions, toutes deux nécessaires.
  //
  // 1. Le studio les accepte. C'est le serveur qui tranche, dans
  //    `checkoutConfig` : il n'envoie un portefeuille que s'il sait le
  //    facturer. Depuis le 16/09/2026 il envoie `applePay: false` en dur et
  //    pas de `googlePay` du tout, le temps que le paiement passe par la
  //    bonne séquence Mindbody (initiate puis complete) ; l'option reste donc
  //    invisible en production sans qu'aucun réglage du back-office ne puisse
  //    la rallumer.
  // 2. L'appareil sait les présenter (cf. utils/wallets.ts). Cette condition
  //    avait disparu le 11/09/2026, ce qui offrait Apple Pay jusque sous
  //    Firefox, où la page de paiement hébergée ne peut que refuser.
  //
  const walletConfig = typeof mondayMindbody !== 'undefined' ? mondayMindbody?.checkoutConfig : undefined;

  // En mode contract, vérifier que la carte stockée couvre toute la période
  // d'engagement (sinon les prélèvements échoueront en cours de route).
  const contractCardCheck =
    isContract && product?.type === 'contract' && clientCreditCard?.ExpYear && clientCreditCard?.ExpMonth
      ? isCardValidThroughContractEngagement(
          { ExpYear: clientCreditCard.ExpYear, ExpMonth: clientCreditCard.ExpMonth },
          product,
        )
      : null;
  const storedCardExpiresBeforeEngagement = !!contractCardCheck && !contractCardCheck.valid;

  // Si on bloque la carte stockée, basculer automatiquement sur "nouvelle carte".
  useEffect(() => {
    if (storedCardExpiresBeforeEngagement) {
      setValue('StoredCreditCard', 'new');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedCardExpiresBeforeEngagement]);

  // En mode contract : forcer carte CB et SaveInfo cochée (autopay obligatoire).
  useEffect(() => {
    if (isContract) {
      setPaymentMethod('Credit Card');
      setValue('PaymentMethod', 'Credit Card');
      setValue('SaveInfo', true);
    }
    // setValue est stable, on l'omet volontairement des deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContract]);

  useEffect(() => {
    if (paymentMethod === 'Alma' && paymentPlans) {
      const defaultPlan = paymentPlans.find((plan) => plan.installmentsCount === 3);
      if (defaultPlan) {
        setSelectedPlan(defaultPlan.installmentsCount);
      }
    }
  }, [paymentMethod, paymentPlans]);

  const handlePaymentChange = (value: string) => {
    setPaymentMethod(value);
    setSelectedPlan(null); // Reset selected plan when payment method changes
  };

  const handlePlanSelect = (planIndex: number) => {
    setSelectedPlan(planIndex);
  };

  // L'adresse enregistrée sur la carte Mindbody (ClientCreditCard.Address)
  // contient souvent rue + code postal + ville sur une seule ligne. Préremplir
  // le champ « Rue » avec, en plus des champs Ville et Code postal, faisait
  // apparaître le code postal deux fois. On ne garde que la rue quand la fin
  // de la ligne répète le code postal / la ville connus.
  const streetOnly = (address: string | undefined, postalCode: string | undefined, city: string | undefined): string => {
    let street = (address ?? '').trim();
    const cityRe = city ? city.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : null;
    if (cityRe) street = street.replace(new RegExp(`[,\\s]+${cityRe}\\s*$`, 'i'), '');
    if (postalCode) street = street.replace(new RegExp(`[,\\s]+${postalCode.trim()}\\s*$`), '');
    if (cityRe) street = street.replace(new RegExp(`[,\\s]+${cityRe}\\s*$`, 'i'), '');
    return street.replace(/[,\s]+$/, '');
  };

  const getDefaultValues = (): CheckoutData => {
    const defaultPostalCode = clientCreditCard?.PostalCode ?? client?.PostalCode ?? '';
    const defaultCity = clientCreditCard?.City ?? client?.City ?? '';
    return {
      AlmaPaymentId: '',
      Amount: amount,
      TotalAmount: amount,
      DiscountedAmount: 0,
      PromoCode: '',
      GiftCard: '',
      BillingName: clientCreditCard?.CardHolder ?? client?.LastName ?? '',
      BillingLastName: client?.LastName ?? '',
      BillingAddress: streetOnly(clientCreditCard?.Address ?? client?.AddressLine1, defaultPostalCode, defaultCity),
      BillingCity: defaultCity,
      BillingPostalCode: defaultPostalCode,
      CreditCardNumber: '',
      Exp: '',
      Cryptogram: '',
      SaveInfo: false,
      StoredCreditCard: clientHasStoredCreditCard ? 'saved' : 'new',
      PaymentMethod: 'Credit Card',
      InstallmentsCount: 0,
      Email: user?.Email ?? '',
      Phone: `00${user?.MobilePhone}`,
      MarketingConsent: hasMarketingConsent(),
      AlmaPaymentStatus: '',
      ServiceId: 0,
    };
  };

  const form = useForm<CheckoutData>({
    defaultValues: getDefaultValues(),
  });

  const {
    setValue,
    getValues,
    register,
    handleSubmit,
    watch,
    unregister,
    control,
    formState: { errors, isSubmitting, isValid },
  } = form;

  // /client/completeinfo répond après le montage : les valeurs par défaut
  // ci-dessus sont alors vides. On préremplit une fois les infos reçues, sans
  // écraser ce que le client aurait déjà saisi.
  const prefilledFromClient = useRef(false);
  useEffect(() => {
    if (prefilledFromClient.current || !client) return;
    prefilledFromClient.current = true;
    const defaults = getDefaultValues();
    (['BillingName', 'BillingAddress', 'BillingCity', 'BillingPostalCode'] as const).forEach((key) => {
      if (!getValues(key) && defaults[key]) setValue(key, defaults[key]);
    });
    // getDefaultValues / getValues / setValue sont stables entre rendus
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const isUsingSavedCreditCard = watch('StoredCreditCard') === 'saved';
  const storedCreditCard = watch('StoredCreditCard');
  const creditCardNumber = watch('CreditCardNumber');
  const cryptogram = watch('Cryptogram');
  const discount = watch('DiscountedAmount');
  const giftCardValue = watch('GiftCardBalance');
  const totalAmount = watch('TotalAmount') ?? 0;
  const productAmount = watch('Amount');

  const effectiveGiftCardValue = giftCardValue ?? 0;
  const remainingAmount = Math.max(totalAmount - effectiveGiftCardValue, 0);
  const vatAmount = remainingAmount * 0.2;

  useEffect(() => {
    if (paymentMethod === 'Alma' && remainingAmount > 0) {
      setIsRefetchingPaymentPlans(true);
      const fetchUpdatedPaymentPlans = async () => {
        try {
          const plans = await HTTP.get<PaymentMethod[]>(`/sale/almapaymentplans`, { remainingAmount }).send();


          if (plans && Array.isArray(plans)) {
            // Filtrer le plan 4x pour les clubs de Marseille (double sécurité côté client)
            const marseilleSites = ['5745580', '5746614', '5745076']; // Dynamo Marseille, Riise Marseille, Punch Marseille
            const isMarseilleClub = mondayMindbody?.siteId && marseilleSites.includes(mondayMindbody.siteId);

            let filteredPlans = plans;
            if (isMarseilleClub) {
              filteredPlans = plans.filter((plan) => plan.installmentsCount !== 4);
            }

            setUpdatedPaymentPlans(filteredPlans);
            const defaultPlan = filteredPlans.find((plan) => plan.installmentsCount === 3);
            if (defaultPlan) {
              setSelectedPlan(defaultPlan.installmentsCount);
            }
          } else {
            setUpdatedPaymentPlans([]);
          }
        } catch (error) {
          Sentry.withScope((scope) => {
            scope.setTag('where', 'CheckoutForm:fetchUpdatedPaymentPlans');
            scope.setTag('paymentMethod', 'alma');
            scope.setExtra('remainingAmount', remainingAmount);
            Sentry.captureException(error);
          });
          setUpdatedPaymentPlans([]);
        } finally {
          setIsRefetchingPaymentPlans(false);
        }
      };

      fetchUpdatedPaymentPlans();
    } else if (paymentMethod !== 'Alma') {
      // Reset payment plans when switching away from Alma
      setUpdatedPaymentPlans(null);
      setSelectedPlan(null);
    }
  }, [paymentMethod, remainingAmount]);

  const giftCardValueCoversTotalAmount = giftCardValue && giftCardValue >= totalAmount;

  // Portefeuilles : deux conditions nécessaires, et trois exclusions.
  //
  // Les conditions : le studio l'a coché en back-office (le serveur le
  // revérifie), et l'appareil sait présenter ce portefeuille
  // (cf. utils/wallets.ts).
  //
  // Les exclusions tiennent à ce que la séquence des moyens alternatifs de
  // Mindbody n'accepte qu'un seul paiement couvrant tout le panier : ni
  // abonnement, qui exige une carte enregistrée pour les prélèvements
  // suivants, ni carte cadeau, qui ne règle qu'une partie, ni commande déjà
  // ramenée à zéro par une remise. Sans ces exclusions, la tuile resterait
  // cliquable pour se faire refuser après coup par le serveur, le client ayant
  // déjà sa carte en main.
  const giftCardCode = watch('GiftCard');
  const isWalletEligibleProduct = product?.type === 'service' && !isContract && remainingAmount > 0 && !giftCardCode;
  const isApplePayAvailable = isWalletEligibleProduct && walletConfig?.applePay === true && isApplePayCapable();
  const isGooglePayAvailable = isWalletEligibleProduct && walletConfig?.googlePay === true && isGooglePayCapable();

  // Fonction pour vérifier si le numéro de carte est une AMEX
  const isAmex = (cardNumber: string) => {
    const amexPattern = /^3[47][0-9]{13}$/;
    return amexPattern.test(cardNumber.replace(/\s/g, ''));
  };

  useEffect(() => {
    // Register fields with conditions based on the payment method and remaining amount
    // Pour Alma : Ces champs seront remplis sur l'interface Alma, pas dans notre formulaire
    register('BillingName', {
      required: remainingAmount > 0 && !isUsingSavedCreditCard && paymentMethod === 'Credit Card',
    });
    register('BillingLastName', { required: false }); // Alma le demande sur son interface
    register('Email', { required: false }); // Alma le demande sur son interface
    register('Phone', { required: false }); // Alma le demande sur son interface
    register('BillingAddress', {
      required:
        remainingAmount > 0 &&
        (paymentMethod === 'Alma' || (!isUsingSavedCreditCard && paymentMethod === 'Credit Card')),
    });
    register('BillingCity', {
      required:
        remainingAmount > 0 &&
        (paymentMethod === 'Alma' || (!isUsingSavedCreditCard && paymentMethod === 'Credit Card')),
    });
    register('BillingPostalCode', {
      required:
        remainingAmount > 0 &&
        (paymentMethod === 'Alma' || (!isUsingSavedCreditCard && paymentMethod === 'Credit Card')),
    });

    // Register CreditCardNumber only if payment is required and using Credit Card
    register('CreditCardNumber', {
      required: remainingAmount > 0 && paymentMethod === 'Credit Card' && !isUsingSavedCreditCard,
      validate: (value) => {
        if (remainingAmount === 0 || paymentMethod !== 'Credit Card' || isUsingSavedCreditCard) return true;
        const cleanedValue = value.replace(/\s/g, '');
        if (isAmex(cleanedValue)) {
          return cleanedValue.length === 15 || 'Numéro de carte AMEX invalide';
        }
        return cleanedValue.length === 16 || 'Numéro de carte invalide';
      },
    });

    // Register Exp only if payment is required and using Credit Card
    register('Exp', {
      required: remainingAmount > 0 && paymentMethod === 'Credit Card' && !isUsingSavedCreditCard,
    });

    // Register Cryptogram only if payment is required and using Credit Card
    register('Cryptogram', {
      required: remainingAmount > 0 && paymentMethod === 'Credit Card' && !isUsingSavedCreditCard,
      pattern: isAmex(creditCardNumber) ? /^[0-9]{4}$/ : /^[0-9]{3}$/,
    });

    register('SaveInfo');
  }, [isUsingSavedCreditCard, creditCardNumber, unregister, register, paymentMethod, remainingAmount]);

  // Cleanup des timers au démontage du composant
  useEffect(() => {
    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
      }
      if (forceRedirectTimerRef.current) {
        clearTimeout(forceRedirectTimerRef.current);
      }
    };
  }, []);

  async function onSubmit(data: CheckoutData) {
    if (paymentInProgress) {

      return;
    }

    setPaymentInProgress(true);

    // Consentement marketing relu ici plutôt qu'au montage : le visiteur a pu
    // répondre au bandeau de cookies entre l'arrivée sur le récapitulatif et
    // la validation du paiement.
    data.MarketingConsent = hasMarketingConsent();

    try {
      // Vérifier la session avant tout paiement Alma
      if (paymentMethod === 'Alma') {
        try {
          const sessionCheck = await HTTP.get<{
            isValid: boolean;
            oauth: { authenticated: boolean; email: string | null };
            session: { hasClientId: boolean; clientId: string | null; email: string | null };
          }>('/auth/check-session').send();

          if (!sessionCheck.isValid) {
            Sentry.captureMessage('Alma payment blocked - invalid session', {
              level: 'warning',
              tags: {
                category: 'payment',
                paymentMethod: 'alma',
                issue: 'session_invalid',
              },
              extra: {
                oauthAuthenticated: sessionCheck.oauth?.authenticated,
                oauthEmail: sessionCheck.oauth?.email,
                hasClientId: sessionCheck.session?.hasClientId,
                sessionEmail: sessionCheck.session?.email,
              },
            });

            toast({
              status: 'error',
              title: 'Session expirée',
              description: 'Votre session a expiré. Veuillez vous reconnecter pour continuer.',
              duration: 5000,
              isClosable: true,
            });

            // Rediriger vers la page de login
            setPaymentInProgress(false);
            const currentPath = location.pathname + location.search + location.hash;
            navigate('/account', {
              state: {
                onLoginRedirect: currentPath,
              },
              replace: true,
            });
            return;
          }
        } catch (sessionError) {
          Sentry.captureException(sessionError, {
            tags: {
              category: 'payment',
              paymentMethod: 'alma',
              issue: 'session_check_failed',
            },
          });
          // En cas d'erreur de vérification, on continue le paiement
          // mais on log l'erreur pour investigation
        }
      }

      // Ensure TotalAmount is never negative (safety check)
      if (data.TotalAmount < 0) {
        data.TotalAmount = 0;
      }

      // If the total amount is 0 (due to promo code or gift card), use Comp payment method
      if (remainingAmount <= 0 || data.TotalAmount === 0) {
        data.PaymentMethod = 'Comp';
        const compResponse = await onPurchase(data);

        // GA4 purchase : achat à 0€ (promo / carte cadeau couvrant le total).
        trackPurchase({
          transactionId: (compResponse as { ShoppingCart?: { Id?: string | number } })?.ShoppingCart?.Id,
          value: 0,
          product: product as unknown as Record<string, unknown>,
          paymentType: 'Comp',
          coupon: data.PromoCode || undefined,
          customer: checkoutCustomer(data),
        });

        // Nouveau Panier (v2) : confirmation rendue dans la page, pas de
        // toast ni de redirection — le composant appelant gère l'étape 3.
        if (onSuccess) {
          onSuccess({ kind: 'comp', response: compResponse });
          return;
        }

        toast({
          status: 'success',
          title: 'Confirmation réussie !',
          description: 'Votre crédit a été ajouté à votre compte.',
          duration: 3000,
          isClosable: true,
        });

        if (from) {
          if (from.pathname === '/tarifs') {
            return navigate('/account/services', { replace: true });
          }
          return navigate(-1);
        }
        return navigate('/', { replace: true });
      }

      if (data.StoredCreditCard === 'new') {
        data.CreditCardNumber = data.CreditCardNumber.replaceAll(/\s/g, '');
      }

      // Only set paymentMethod if it's not already set to Comp (to avoid overriding the Comp method set above)
      if (data.PaymentMethod !== 'Comp') {
        data.PaymentMethod = paymentMethod;
      }

      // Traitement spécial pour les paiements Alma
      if (paymentMethod === 'Alma' && selectedPlan !== null) {
        data.InstallmentsCount = selectedPlan;
        data.ServiceId = product!.Id as number;

        // GA4 : mémoriser l'item complet avant la redirection Alma (le purchase
        // est tracké au retour, où seul l'id survit en session).
        stashPurchaseItem(product as unknown as Record<string, unknown>);
        stashPurchaseCustomer(checkoutCustomer(data));

        Sentry.captureMessage('Alma payment initiated', {
          level: 'info',
          tags: {
            category: 'payment',
            paymentMethod: 'alma',
          },
          extra: {
            selectedPlan,
            amount: data.TotalAmount,
            user: `${user?.FirstName} ${user?.LastName}`,
            email: user?.Email,
          },
        });

        const response: Record<string, unknown> = await onPurchase(data);

        if (response) {
          data.AlmaPaymentStatus = 'pending';

          // 🔒 SÉCURITÉ : Stocker le payment_id d'Alma pour la vérification anti-replay
          // Cela permet de garantir qu'un paiement Alma ne peut être utilisé qu'une seule fois
          if (response.id && typeof response.id === 'string') {
            data.AlmaPaymentId = response.id;
          } else {
            // Cas anormal : le payment_id devrait toujours être présent
            Sentry.captureMessage('Alma payment ID missing in response', {
              level: 'warning',
              tags: {
                category: 'payment',
                paymentMethod: 'alma',
                issue: 'missing_payment_id',
              },
              extra: {
                response,
                selectedPlan,
              },
            });
          }

          // safeSessionStorage : un storage indisponible ne doit pas bloquer la redirection Alma.
          safeSessionStorage.setItem('paymentData', JSON.stringify(data));

          // Activer la protection contre les double-clics lors de la redirection
          setAlmaRedirectInProgress(true);

          Sentry.startSession();

          Sentry.captureMessage('Alma payment redirect', {
            level: 'info',
            tags: {
              category: 'payment',
              paymentMethod: 'alma',
            },
            extra: {
              redirectUrl: response.url,
              paymentId: response.id,
            },
          });

          return (window.location.href = response?.url as string);
        }
      }

      // Portefeuilles (Apple Pay, Google Pay) : séquence des moyens alternatifs
      // de Mindbody, qui n'a rien de commun avec le 3D Secure. Le serveur ouvre
      // la commande et nous rend l'adresse de la page de paiement hébergée ;
      // c'est le navigateur qui s'y rend, le portefeuille étant sur l'appareil
      // du client. Le retour arrive sur #/wallet-callback, qui finalise.
      if (WALLET_METHODS.includes(paymentMethod)) {
        const baseUrl = window.location.href.split('#')[0];

        // GA4 : mémoriser la commande avant de quitter le site.
        stashPurchaseItem(product as unknown as Record<string, unknown>);
        stashPurchaseCustomer(checkoutCustomer(data));

        setWalletStep('redirecting');

        try {
          const wallet = (await HTTP.post('/sale/walletinitiate', {
            ServiceId: product!.Id,
            Payment: data,
            ReturnUrl: `${baseUrl}#/wallet-callback`,
          }).send()) as { RedirectUrl?: string };

          if (!wallet?.RedirectUrl) {
            throw new Error('WalletRedirectMissing');
          }

          window.location.href = wallet.RedirectUrl;
          return;
        } catch (error) {
          // L'overlay se referme : le client retrouve son panier intact et le
          // message d'erreur habituel, sans être parti nulle part.
          setWalletStep('idle');
          throw error;
        }
      }

      // Traitement des paiements par carte de crédit avec SCA : Mindbody
      // renvoie une AuthenticationUrl et le retour passe par /sca-callback qui
      // re-poste le panier avec les TransactionIDs.
      if (paymentMethod === 'Credit Card') {
        // Configuration de l'URL de retour pour SCA
        const currentUrl = window.location.href;
        const baseUrl = currentUrl.split('#')[0];
        // attempt_id : propagé dans l'URL de retour 3DS pour corréler dans
        // Sentry l'event « redirect initiated » avec l'event de retour
        // (completed OU « payment data missing »), même si le storage est
        // vide au retour (le fragment survit à la redirection Stripe).
        const scaAttemptId = newScaAttemptId();
        data.ScaReturnUrl = `${baseUrl}#/sca-callback?attempt=${scaAttemptId}`;

        // GA4 : mémoriser l'item complet avant une éventuelle redirection 3DS
        // (service OU contract : ContractCheckout redirige pendant onPurchase).
        stashPurchaseItem(product as unknown as Record<string, unknown>);
        stashPurchaseCustomer(checkoutCustomer(data));

        const response: Record<string, unknown> = await onPurchase(data);

        // En mode contract, ContractCheckout.onPurchase gère lui-même la
        // détection SCA + le stockage sessionStorage + la redirection (clé
        // sessionStorage différente). On ne fait que la redirection succès.
        if (!isContract && response && response.sca3DSecureRequired) {

          // Sauvegarder les données pour le second appel après authentification
          const scaData = {
            ...data,
            ServiceId: product!.Id as number,
            TransactionIDs: response.scaTransactionIds,
            originalResponse: response.originalResponse,
          };
          const scaPersisted = setScaPayment('scaPaymentData', scaData, 'service');

          // Amorce 3DS. Un event par paiement carte (357 en deux jours sur les
          // 5 000 erreurs mensuelles du plan Free) : le cas nominal n'est plus
          // qu'un breadcrumb, et l'event ne part que si la persistance a échoué,
          // ce qui annonce un retour « données absentes ». Le taux de complétion
          // se lit côté serveur dans logs/payment/ (Logger::payment), plus dans
          // Sentry. `sca_attempt` + `device_id` : à
          // rapprocher des MÊMES tags côté callback — un device_id différent ou
          // absent au retour prouve un changement de contexte navigateur.
          const scaInitiated = {
            tags: {
              flow: 'sca',
              sca_stage: 'persist',
              payment_kind: 'service',
              sca_attempt: scaAttemptId,
              device_id: getOrCreateDeviceId() ?? 'none',
              persisted_local: String(scaPersisted.local),
              persisted_session: String(scaPersisted.session),
            },
            extra: { persisted: scaPersisted },
          };
          if (scaPersisted.local || scaPersisted.session) {
            sentryTrace('SCA: 3DS redirect initiated', scaInitiated);
          } else {
            Sentry.captureMessage('SCA: 3DS redirect initiated', { level: 'warning', ...scaInitiated });
          }

          // Redirection vers la première URL d'authentification
          const authUrls = response.scaAuthenticationUrls as string[];
          if (authUrls && authUrls.length > 0) {
            window.location.href = authUrls[0];
            return;
          }
        }

        // Si SCA contract : ContractCheckout a déjà fait window.location.href.
        // On stoppe ici pour ne pas exécuter handleSuccessfulPayment.
        if (isContract && response && response.sca3DSecureRequired) {
          return;
        }

        // Paiement réussi sans SCA
        // GA4 purchase : paiement carte abouti sans challenge 3DS.
        // (Le cas SCA est suivi côté ScaCallback après authentification.)
        // NB : /sale/purchasecontract ne renvoie PAS de ShoppingCart (schéma
        // Mindbody : ClientId, ContractId, ClientContractId, Totals…). Pour un
        // abonnement, le transaction_id est le ClientContractId (unique par
        // souscription).
        const cardResponse = response as { ShoppingCart?: { Id?: string | number }; ClientContractId?: number | null };
        trackPurchase({
          transactionId: cardResponse?.ShoppingCart?.Id ?? cardResponse?.ClientContractId ?? undefined,
          value: data.TotalAmount,
          product: product as unknown as Record<string, unknown>,
          paymentType: paymentMethod,
          coupon: data.PromoCode || undefined,
          customer: checkoutCustomer(data),
        });

        await handleSuccessfulPayment();
        return;
      }

      // Fallback pour autres méthodes de paiement
      const fallbackResponse = await onPurchase(data);

      // GA4 purchase : méthode de paiement autre (ex. moyen custom).
      trackPurchase({
        transactionId: (fallbackResponse as { ShoppingCart?: { Id?: string | number } })?.ShoppingCart?.Id,
        value: data.TotalAmount,
        product: product as unknown as Record<string, unknown>,
        paymentType: data.PaymentMethod || paymentMethod,
        coupon: data.PromoCode || undefined,
        customer: checkoutCustomer(data),
      });

      await handleSuccessfulPayment();
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setTag('where', 'CheckoutForm:onSubmit');
        scope.setTag('paymentKind', isContract ? 'contract' : 'service');
        scope.setExtra('paymentMethod', paymentMethod);
        scope.setExtra('selectedPlan', selectedPlan);
        scope.setExtra('checkoutData', data);
        scope.setExtra('payment_details', {
          selectedPlan,
          amount: data.Amount,
          totalAmount: data.TotalAmount,
          discountedAmount: data.DiscountedAmount,
          serviceId: data.ServiceId,
          isUsingSavedCreditCard: isUsingSavedCreditCard,
          giftCardValue: giftCardValue,
          hasPromoCode: !!data.PromoCode,
          product: JSON.stringify(product),
          scaSupport: {
            exemptionRequested: data.ScaExemptionRequested,
            authenticationRequired: data.Sca3DSecureRequired,
            transactionId: data.ScaTransactionId,
          },
        });

        if (paymentMethod === 'Alma' && selectedPlan !== null) {
          scope.setExtra('almaSpecificData', {
            InstallmentsCount: data.InstallmentsCount,
            ServiceId: data.ServiceId,
          });
        }
        if (e instanceof SyntaxError && e.message.includes('JSON.parse')) {
          scope.setTag('error_type', 'json_parse_error');
          scope.setLevel('error');
          Sentry.captureException(e, {
            extra: {
              details: 'Erreur lors du parsing de la réponse du serveur',
              originalError: e,
            },
          });
        } else if (e instanceof ApiClientException) {
          scope.setTag('error_type', 'api_client_error');
          scope.setExtra('api_error_details', {
            errorCode: e.ErrorCode,
            errors: e.errors,
            message: e.getMessage(),
          });
          Sentry.captureException(e);
        } else {
          scope.setTag('error_type', 'unknown_error');
          Sentry.captureException(e);
        }
      });

      if (e instanceof ApiClientException) {
        const title = e.getMessage();
        const errors = e.errors || [];

        if (errors.length === 0) {
          toast({
            status: 'error',
            title: title,
          });
        } else {
          errors.forEach((error: { message: string }) => {
            toast({
              status: 'error',
              title: error.message || title,
            });
          });
        }

        if (e.ErrorCode === ApiClientExceptionErrorCode.GiftCardBalanceMismatch) {
          setValue('GiftCardBalance', undefined);
          setValue('GiftCard', '');
        }
      } else {
        const { title, description } = resolveUserError('checkout', e);
        toast({
          status: 'error',
          title,
          description,
          duration: 8000,
          isClosable: true,
        });
      }
    } finally {
      // Ne pas réinitialiser paymentInProgress si c'est un paiement Alma qui redirige
      if (!(paymentMethod === 'Alma' && almaRedirectInProgress)) {
        setPaymentInProgress(false);
      }
    }
  }

  // Fonction pour gérer un paiement réussi
  const handleSuccessfulPayment = async () => {
    // Nouveau Panier (v2) : l'appelant affiche l'étape « Confirmation »
    // dans la page. Pas de toast, pas de timers de secours, pas de navigate.
    if (onSuccess) {
      onSuccess({ kind: 'paid', paymentMethod });
      return;
    }

    toast({
      status: 'success',
      title: isContract ? 'Abonnement activé !' : 'Paiement réussi !',
      description: isContract
        ? "Votre abonnement peut apparaître dans Mon Compte d'ici 1 minute. Redirection en cours..."
        : 'Votre crédit a été ajouté à votre compte. Redirection en cours...',
      duration: 5000,
      isClosable: true,
    });


    // Pour les paiements CB, démarrer les timers après le paiement réussi
    if (paymentMethod === 'Credit Card') {
      fallbackTimerRef.current = setTimeout(() => {

        Sentry.captureMessage('Fallback redirect button shown', {
          level: 'warning',
          tags: {
            category: 'payment_redirect',
            issue: 'redirect_delay',
          },
          extra: {
            paymentMethod: 'Credit Card',
            userEmail: user?.Email,
            from: from?.pathname,
          },
        });
        setShowFallbackRedirect(true);
      }, 10000);

      forceRedirectTimerRef.current = setTimeout(() => {

        Sentry.captureMessage('Force redirect triggered', {
          level: 'error',
          tags: {
            category: 'payment_redirect',
            issue: 'force_redirect_needed',
          },
          extra: {
            paymentMethod: 'Credit Card',
            userEmail: user?.Email,
            from: from?.pathname,
          },
        });
        navigate(isContract ? '/account#mon-abonnement' : '/account/services', { replace: true });
      }, 15000);
    }

    const cleanupTimers = () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
      if (forceRedirectTimerRef.current) {
        clearTimeout(forceRedirectTimerRef.current);
        forceRedirectTimerRef.current = null;
      }
    };

    const redirectToAccount = () => {

      cleanupTimers();
      // En mode contract, l'abonnement apparaît dans le bloc "Mon Abonnement"
      // et non dans "Mes crédits". On redirige vers l'ancre dédiée.
      if (isContract) {
        navigate('/account#mon-abonnement', { replace: true });
        return;
      }
      navigate('/account/services', { replace: true });
    };

    // En mode contract, on redirige toujours vers le bloc abonnement, peu
    // importe d'où on vient (ContractCard ne transmet pas `from`).
    if (isContract) {
      return redirectToAccount();
    }

    if (from) {
      if (from.pathname === '/tarifs') {
        return redirectToAccount();
      }
      cleanupTimers();
      return navigate(-1);
    }

    cleanupTimers();
    navigate('/', { replace: true });
  };

  const handleFallbackRedirect = () => {

    // Nettoyer les timers puisque l'utilisateur a cliqué manuellement
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    if (forceRedirectTimerRef.current) {
      clearTimeout(forceRedirectTimerRef.current);
      forceRedirectTimerRef.current = null;
    }

    navigate('/account/services', { replace: true });
  };


  const submitEnabled =
    isUsingSavedCreditCard ||
    giftCardValueCoversTotalAmount ||
    isValid ||
    (paymentMethod === 'Alma' && selectedPlan !== null) ||
    WALLET_METHODS.includes(paymentMethod) ||
    (isAmex(creditCardNumber) && cryptogram.length === 4);

  const getProductPrice = () => {
    if (product?.type === 'giftcard') {
      if (product.SalePrice === 0) {
        return getValues('Amount');
      }

      return product.SalePrice.toFixed(2);
    }

    if (product?.type === 'contract') {
      const recurring = product.RecurringPaymentAmountTotal || product.FirstPaymentAmountTotal || 0;
      return recurring.toFixed(2);
    }

    return product?.type === 'service' ? product.Price.toFixed(2) : '0.00';
  };

  // Add a function to check if Alma is selectable
  const isAlmaSelectable = remainingAmount >= 100;

  // Fonction pour déterminer le message du tooltip selon les différents cas de désactivation
  const getDisabledTooltipMessage = (): string => {
    // Cas 1: Offre d'essai déjà achetée
    if (disableConfirmAlmaPayment) {
      return "Vous avez déjà acheté cette offre d'essai. Ce type d'offre n'est disponible qu'une seule fois par client.";
    }

    // Cas 2: Paiement en cours
    if (paymentInProgress || isSubmitting || almaRedirectInProgress) {
      return 'Paiement en cours de traitement, veuillez patienter...';
    }

    // Cas 3: Paiement Alma sans plan sélectionné
    if (paymentMethod === 'Alma' && selectedPlan === null && remainingAmount > 0) {
      return 'Veuillez choisir un échéancier Alma';
    }

    // Cas 4: Paiement Alma sans plans disponibles
    if (
      paymentMethod === 'Alma' &&
      remainingAmount > 0 &&
      updatedPaymentPlans !== null &&
      updatedPaymentPlans.length === 0
    ) {
      return 'Aucun plan de paiement Alma disponible pour ce montant. Veuillez choisir un autre mode de paiement.';
    }

    // Cas 5: Formulaire invalide - détails spécifiques
    if (remainingAmount > 0 && !submitEnabled) {
      const missingFields: string[] = [];

      // Pour Alma : Les champs sont remplis sur l'interface Alma, pas ici
      // On ne vérifie rien car le bouton est désactivé uniquement si aucun plan n'est sélectionné
      // (déjà géré dans le Cas 3 ci-dessus)

      // Vérifications pour carte de crédit (nouvelle carte)
      if (paymentMethod === 'Credit Card' && !isUsingSavedCreditCard) {
        if (!getValues('BillingName')) missingFields.push('Nom de facturation');
        if (!getValues('BillingAddress')) missingFields.push('Adresse');
        if (!getValues('BillingCity')) missingFields.push('Ville');
        if (!getValues('BillingPostalCode')) missingFields.push('Code postal');

        const cardNumber = getValues('CreditCardNumber');
        const exp = getValues('Exp');
        const crypto = getValues('Cryptogram');

        if (!cardNumber || cardNumber.replace(/\s/g, '').length < 15) {
          missingFields.push('Numéro de carte valide');
        }
        if (!exp || !exp.match(/[0-1][0-9] \/ [0-9][0-9]/)) {
          missingFields.push("Date d'expiration valide");
        }
        if (!crypto || (isAmex(cardNumber) ? crypto.length !== 4 : crypto.length !== 3)) {
          missingFields.push('Cryptogramme valide');
        }
      }

      if (missingFields.length > 0) {
        return `Veuillez remplir les champs suivants : ${missingFields.join(', ')}`;
      }

      return 'Veuillez remplir tous les champs obligatoires';
    }

    return '';
  };


  return {
    // form
    form,
    register,
    control,
    errors,
    isSubmitting,
    handleSubmit,
    onSubmit,
    // produit / montants
    product,
    productAmount,
    totalAmount,
    remainingAmount,
    vatAmount,
    discount,
    giftCardValue,
    giftCardValueCoversTotalAmount,
    getProductPrice,
    // client / carte enregistrée
    clientCompleteInfo,
    clientCreditCard,
    clientHasStoredCreditCard,
    storedCardExpiresBeforeEngagement,
    isUsingSavedCreditCard,
    storedCreditCard,
    creditCardNumber,
    isAmex,
    // moyens de paiement
    paymentMethod,
    handlePaymentChange,
    customPaymentMethods,
    isAlmaAvailable,
    isAlmaSelectable,
    isApplePayAvailable,
    isGooglePayAvailable,
    walletStep,
    isLoadingPaymentPlans,
    isRefetchingPaymentPlans,
    updatedPaymentPlans,
    selectedPlan,
    handlePlanSelect,
    // état de soumission
    paymentInProgress,
    almaRedirectInProgress,
    showFallbackRedirect,
    handleFallbackRedirect,
    submitEnabled,
    disableConfirmAlmaPayment,
    getDisabledTooltipMessage,
    // contexte
    isContract,
    contractAgreement,
    isLoggedIn,
    navigate,
    t,
  };
}
